import React, { useEffect, useState, useRef } from 'react';
import { Mail, Smartphone, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Header } from '../components/ui/Header';
export function ForgotPasswordScreen() {
  const {
    goBack,
    resetTo
  } = useNavigation();
  // Flow State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  // Step 1 State
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Step 2 State
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(60);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // Step 3 State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Timer Effect for Step 2
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);
  // Handlers
  const handleSendCode = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep(2);
      setTimer(60);
    }, 1500);
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value[0];
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value !== '' && index < 3) {
      inputs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };
  const handleVerifyCode = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep(3);
    }, 1000);
  };
  const handleResetPassword = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep(4);
    }, 1500);
  };
  // Helpers
  const maskedContact = method === 'email' ? email.replace(/(.{2})(.*)(?=@)/, (gp1, gp2, gp3) => gp2 + '*'.repeat(gp3.length)) : phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
  const isOtpComplete = otp.every(digit => digit !== '');
  const isPasswordValid = newPassword.length >= 8 && newPassword === confirmPassword;
  return <div className="min-h-screen bg-white flex flex-col overflow-hidden" data-id="element-522">
      <Header transparent showBack={step === 1} data-id="element-523" />

      {/* Custom Back Button for inner steps */}
      {step > 1 && step < 4 && <button onClick={() => setStep(step - 1)} className="absolute top-3 left-2 p-2 rounded-full hover:bg-gray-100 transition-colors z-50" data-id="element-524">
          <svg className="w-6 h-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-525">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-526" />
          </svg>
        </button>}

      <div className="flex-1 p-6 flex flex-col relative" data-id="element-527">
        <AnimatePresence mode="wait" data-id="element-528">
          {/* STEP 1: Enter Email/Phone */}
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-529">
              <div className="mb-8" data-id="element-530">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-531">
                  Reset Password
                </h1>
                <p className="text-gray-500" data-id="element-532">
                  Enter your email or phone number to receive a verification
                  code
                </p>
              </div>

              <div className="flex bg-gray-100 rounded-xl p-1 mb-6" data-id="element-533">
                <button onClick={() => setMethod('email')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${method === 'email' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-534">
                  <Mail className="w-4 h-4" data-id="element-535" />
                  Email
                </button>
                <button onClick={() => setMethod('phone')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${method === 'phone' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-536">
                  <Smartphone className="w-4 h-4" data-id="element-537" />
                  Phone
                </button>
              </div>

              <div className="flex flex-col gap-4 mb-8" data-id="element-538">
                {method === 'email' ? <Input label="Email Address" type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-540" />} data-id="element-539" /> : <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" value={phone} onChange={e => setPhone(e.target.value)} icon={<Smartphone className="w-5 h-5" data-id="element-542" />} data-id="element-541" />}
              </div>

              <Button fullWidth size="lg" onClick={handleSendCode} loading={loading} disabled={method === 'email' ? !email : !phone} data-id="element-543">
                Send Verification Code
              </Button>
            </motion.div>}

          {/* STEP 2: Enter OTP */}
          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-544">
              <div className="mb-8" data-id="element-545">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-546">
                  Verification Code
                </h1>
                <p className="text-gray-500" data-id="element-547">
                  Enter the 4-digit code sent to{' '}
                  <span className="font-medium text-gray-900" data-id="element-548">
                    {maskedContact}
                  </span>
                </p>
              </div>

              <div className="flex justify-between gap-4 mb-8" data-id="element-549">
                {otp.map((digit, idx) => <input key={idx} ref={el => inputs.current[idx] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(idx, e.target.value)} onKeyDown={e => handleOtpKeyDown(idx, e)} className={`w-14 h-14 rounded-xl border-2 text-center text-2xl font-bold focus:outline-none transition-colors ${digit ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-900 focus:border-primary/50'}`} data-id="element-550" />)}
              </div>

              <div className="text-center mb-8" data-id="element-551">
                {timer > 0 ? <p className="text-sm text-gray-500" data-id="element-552">
                    Resend code in{' '}
                    <span className="font-bold text-primary" data-id="element-553">
                      0:{timer.toString().padStart(2, '0')}
                    </span>
                  </p> : <button onClick={() => setTimer(60)} className="text-sm font-bold text-primary hover:underline" data-id="element-554">
                    Resend Code
                  </button>}
              </div>

              <Button fullWidth size="lg" onClick={handleVerifyCode} loading={loading} disabled={!isOtpComplete} data-id="element-555">
                Verify Code
              </Button>
            </motion.div>}

          {/* STEP 3: New Password */}
          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-556">
              <div className="mb-8" data-id="element-557">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-558">
                  Create New Password
                </h1>
                <p className="text-gray-500" data-id="element-559">
                  Your new password must be different from previously used
                  passwords.
                </p>
              </div>

              <div className="flex flex-col gap-5 mb-8" data-id="element-560">
                <Input label="New Password" type={showNew ? 'text' : 'password'} placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} icon={<Lock className="w-5 h-5" data-id="element-562" />} rightIcon={<button onClick={() => setShowNew(!showNew)} className="p-1 hover:text-primary transition-colors" data-id="element-563">
                      {showNew ? <EyeOff className="w-5 h-5" data-id="element-564" /> : <Eye className="w-5 h-5" data-id="element-565" />}
                    </button>} data-id="element-561" />

                <Input label="Confirm Password" type={showConfirm ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} icon={<Lock className="w-5 h-5" data-id="element-567" />} rightIcon={<button onClick={() => setShowConfirm(!showConfirm)} className="p-1 hover:text-primary transition-colors" data-id="element-568">
                      {showConfirm ? <EyeOff className="w-5 h-5" data-id="element-569" /> : <Eye className="w-5 h-5" data-id="element-570" />}
                    </button>} error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined} data-id="element-566" />
              </div>

              <Button fullWidth size="lg" onClick={handleResetPassword} loading={loading} disabled={!isPasswordValid} data-id="element-571">
                Reset Password
              </Button>
            </motion.div>}

          {/* STEP 4: Success */}
          {step === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex-1 flex flex-col items-center justify-center text-center" data-id="element-572">
              <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mb-6 text-success" data-id="element-573">
                <CheckCircle2 className="w-12 h-12" data-id="element-574" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-575">
                Password Reset Successful
              </h2>
              <p className="text-gray-500 mb-8 px-4" data-id="element-576">
                You have successfully reset your password. Please use your new
                password when logging in.
              </p>
              <Button fullWidth size="lg" onClick={() => resetTo(Screen.LOGIN)} data-id="element-577">
                Back to Login
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}